import React from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import { styleOverrideReason } from '@ids/core';
import { themes, getTheme, ThemeContext } from '@ids/theme';
import { getA11yMessage, setA11yMessage, cleanA11yMessage } from './a11yHelper';

import {
  Wrapper,
  IconBox,
  AddNewItem,
  AddNewTextContainer,
  AddNewValueContainer,
  MenuWrapper,
  TextFieldWrapper,
  AddNewIcon,
  DownIconWrapper
} from './StyledDropdownTypeahead';

const { arrayOf, bool, func, object, string } = PropTypes;

/** */
export default class DropdownTypeahead extends React.PureComponent {
  static contextType = ThemeContext;

  static propTypes = {
    /**
     * Used for accessibility purposes.
     */
    'aria-label': string,
    /**
     * Used for accessibility purposes.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    'aria-required': bool,
    /**
     * Determine whether DropdownTypeahead render add-new Menu Item
     */
    // eslint-disable-next-line react/boolean-prop-naming
    addNew: bool,
    /**
     * The text for add-new Menu Item, default is 'Add'
     */
    addNewText: string,
    /**
     * The props need to pass down to add-new Menu Item
     */
    // eslint-disable-next-line react/forbid-prop-types
    addNewItemProps: object,
    /**
     * Determine whether it shows checkmark for selected Menu Item
     */
    // eslint-disable-next-line react/boolean-prop-naming
    showCheckmark: bool,
    /**
     * Enable Multi Select dropdown if true
     */
    // eslint-disable-next-line react/boolean-prop-naming
    multiselect: bool,
    /**
     * Id used for automation testing
     */
    automationId: string,
    /**
     * The dataSource is array of object used for rendering MenuItems.
     */
    dataSource: arrayOf(object).isRequired,
    /**
     * Disable dropdown if true.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Error message to be rendered.
     */
    errorText: string,
    /**
     * helper message to be rendered.
     */
    helperText: string,
    /**
     * Id for component.
     */
    id: string,
    /**
     * A string value need to pass down to TextField.
     */
    inputValue: string.isRequired,
    /**
     * Label to be displayed for the TextField.
     */
    label: string,
    /**
     * Class to be applied to menu.
     */
    menuClass: string,
    /**
     * Callback function to execute on DropdownTypeahead get blurred.
     */
    onBlur: func,
    /**
     * Callback function to be executed when a MenuItem gets selected.
     * The signature is: onChange(event, { selectedItem, method }),
     * where 'event' - the actual DOM event object, which is either the menu-item div or text-input,
     *       'selectedItem' - the selected item,
     *       'highlightedIndex' - the index of highlighted item in Menu,
     *       'method' - string to tell how user selected the item: 'click' or 'enter'.
     */
    onChange: func,
    /**
     * Callback function to be executed when the DropdownTypeahead gets clicked.
     */
    onClick: func,
    /**
     * Callback function to be executed when the DropdownTypeahead gets focus.
     */
    onFocus: func,
    /**
     * Callback function to execute when user press a key in the TextField.
     * The signature is: onKeyDown(event, { highlightedItem, highlightedIndex }),
     * where 'event' - the actual DOM event object, which is text-input,
     *       'highlightedItem' - the highlighted item,
     *       'highlightedIndex' - the index of highlighted item in Menu,
     * If key name is 'Enter' and an item is highlighted, it will fire onChange callback as well.
     */
    onKeyDown: func,
    /**
     * Callback function to execute when user typing in the TextField.
     */
    onSearch: func,
    /**
     * Callback function to be executed when the menu gets closed.
     */
    onClose: func,
    /**
     * Placeholder to be displayed for the TextField.
     */
    placeholder: string,
    /**
     * preventMenuOverflow: an object variable; used to prevent DropdownTypeahead Menu from being positioned outside the boundary.
     *  More detail in @ids/position preventOverflow API document.
     */
    // eslint-disable-next-line react/forbid-prop-types
    preventMenuOverflow: object,
    /**
     * A required function is used for rendering MenuItems.
     */
    renderItem: func.isRequired,
    /**
     * An option to select all input text when DropdownTypeahead gets keyboard TAB in focused.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    selectTextOnFocus: bool,
    /**
     * The optional styles applied to the position node wrapped around the root node.
     */
    // eslint-disable-next-line react/forbid-prop-types
    stylePosition: object,
    /**
     * suggestedText to be displayed for the TextField.
     */
    suggestedText: string,
    /**
     * When there is an application level theme defined, use this prop to override
     * (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: PropTypes.oneOf(themes),
    /**
     * Selected value.
     */
    value: PropTypes.oneOfType([string, arrayOf(string)]).isRequired,
    /**
     * warning message to be rendered.
     */
    warningText: string,
    /**
     * Width prop to set a custom width needed for TextField
     */
    width: string,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    'aria-label': undefined,
    'aria-required': false,
    addNew: false,
    addNewText: 'Add',
    addNewItemProps: null,
    showCheckmark: false,
    multiselect: false,
    automationId: undefined,
    disabled: false,
    errorText: '',
    helperText: '',
    id: undefined,
    label: '',
    menuClass: undefined,
    onBlur() {},
    onChange() {},
    onClick() {},
    onFocus() {},
    onKeyDown() {},
    onSearch() {},
    onClose() {},
    placeholder: undefined,
    preventMenuOverflow: undefined,
    selectTextOnFocus: false,
    stylePosition: null,
    suggestedText: undefined,
    theme: undefined,
    warningText: '',
    width: undefined,
    styleOverrideReason: undefined
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      highlightIndex: -1
    };
    this.inputRef = null;
    this.menuListRef = null;
    this.parentWidth = 0;
    this.rootElement = React.createRef();
    this.itemSelected = !!props.value;
    this.items = [];
    this.selectedIndex = -1;
    this.autofilled = false; // this flag only for addNew
    this.prevInputValue = props.inputValue || null; // this flag only for addNew
    this.ariaDTAId = uniqueId('idsDropdownTypeahead');
    this.menuId = `${this.ariaDTAId}-idsMenu`;
    this.ariaIdTextField = uniqueId('idsDropdownTypeaheadTextField');
    if (props.stylePosition) {
      console.warn(
        'stylePosition prop was added previously as a hack for zIndex issue. We now have permanent solution for issue. Please refrain from using this prop'
      );
    }
  }

  componentDidMount() {
    if (this.rootElement && this.rootElement.current) {
      this.parentWidth = this.rootElement.current.offsetWidth;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !this.props.multiselect &&
      this.props.value &&
      this.props.value !== prevProps.value
    ) {
      if (prevState.open) {
        this.handleMenuClose();
      }
      // workaround for updating Menu position when it opens on top and shrinks
    } else if (
      this.state.open &&
      this.menuListRef &&
      this.menuListRef.getAttribute('data-placement') &&
      this.menuListRef.getAttribute('data-placement').indexOf('top') > -1 &&
      prevProps.dataSource.length !== this.props.dataSource.length
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ open: false }, () => {
        this.setState({ open: true });
      });
    }

    // need to be a little delay for the screen reader
    setTimeout(() => {
      this.updateA11yMessage();
    }, 200);
  }

  componentWillUnmount() {
    cleanA11yMessage();
  }

  getActiveElement = event => event.relatedTarget || document.activeElement;

  getMenuItemId = index => `${this.ariaDTAId}-item-${index}`;

  getAddNewValue = inputValue => {
    if (this.itemSelected || this.autofilled) {
      return null;
    }

    return inputValue || null;
  };

  setHighlightIndex = event => {
    if (!this.menuListRef) {
      return;
    }

    const keyName = event.key;
    const itemCount = this.items.length;
    let { highlightIndex } = this.state;
    if (this.selectedIndex > -1 && highlightIndex === -1) {
      highlightIndex = this.selectedIndex;
    } else {
      if (keyName === 'ArrowDown') {
        highlightIndex =
          highlightIndex === -1 ? 0 : (highlightIndex + 1) % itemCount;
      }

      if (keyName === 'ArrowUp') {
        highlightIndex =
          highlightIndex === -1
            ? itemCount - 1
            : (highlightIndex - 1 + itemCount) % itemCount;
      }
    }

    // call prop.onKeyDown, since setHighlightIndex is executed inside TextField onKeydown
    this.props.onKeyDown(event, {
      highlightedItem: this.items[highlightIndex] || {},
      highlightedIndex: highlightIndex
    });

    if (highlightIndex > -1) {
      this.setState({ highlightIndex }, () => {
        if (this.props.addNew) {
          this.autofilled = this.props.inputValue !== this.prevInputValue;
          this.prevInputValue = this.props.inputValue;
        }
      });
    }
  };

  updateA11yMessage() {
    const { open } = this.state;
    const { inputValue, suggestedText } = this.props;
    const messageObject = {
      inputValue,
      itemsCount: this.items.length,
      itemSelected: this.itemSelected,
      open,
      suggestedText
    };
    const a11yMessage = getA11yMessage(messageObject);
    setA11yMessage(a11yMessage);
  }

  handleGetSelectedIndex = index => {
    this.selectedIndex = index || this.selectedIndex;
  };

  handleMenuClose = callback => {
    this.setState({ open: false, highlightIndex: -1 }, () => {
      if (this.props.addNew) {
        this.autofilled = false;
      }

      this.props.onClose();

      if (typeof callback === 'function') {
        callback();
      }
    });
  };

  handleTextFieldBlur = event => {
    event.stopPropagation();
    event.preventDefault();
    event.persist();
    const activeElement = this.getActiveElement(event);
    const { open } = this.state;
    this.selectedIndex = -1;
    if (
      open &&
      !event.target.contains(activeElement) &&
      !this.menuListRef.contains(activeElement) &&
      this.menuListRef !== activeElement
    ) {
      this.handleMenuClose(() => {
        this.props.onBlur(event);
      });
    } else if (!open) {
      this.props.onBlur(event);
    }
  };

  handleTextFieldChange = event => {
    // Autofill key is 'Unidentified'.
    const keyName = event.key;
    const { open } = this.state;
    if (keyName === 'Unidentified') {
      return;
    }

    if (['ArrowDown', 'ArrowUp', 'Escape', 'Tab'].indexOf(keyName) === -1) {
      // eslint-disable-next-line no-negated-condition
      if (!open) {
        this.setState({ open: true });
      } else {
        this.setState({ highlightIndex: -1 });
      }

      this.selectedIndex = -1;
      if (this.props.addNew) {
        this.autofilled = false;
      }

      this.props.onSearch(event);
    }
  };

  handleTextFieldKeyDown = event => {
    event.stopPropagation();
    // Autofill key is 'Unidentified'.
    const keyName = event.key;
    if (keyName === 'Unidentified') {
      return;
    }

    const { onChange, onKeyDown } = this.props;
    const { highlightIndex, open } = this.state;
    const highlightObject = {
      highlightedItem: this.items[highlightIndex] || {},
      highlightedIndex: highlightIndex
    };

    switch (keyName) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        // eslint-disable-next-line no-negated-condition
        if (!open) {
          event.persist();
          // only ArrowDown or ArrowUp key event can highlight an item
          this.setState({ open: true }, () => {
            this.setHighlightIndex(event);
          });
        } else {
          this.setHighlightIndex(event);
        }

        break;
      case 'Enter':
        if (open) {
          event.preventDefault();
          if (highlightIndex > -1) {
            this.selectedIndex = highlightIndex;
            if (!this.props.multiselect) {
              this.handleMenuClose();
            }

            onChange(event, {
              selectedItem: this.items[highlightIndex],
              highlightedIndex: highlightIndex,
              method: 'enter'
            });
          }
        }

        onKeyDown(event, highlightObject);
        break;
      case 'Escape':
        event.preventDefault();
        if (open) {
          this.handleMenuClose(event);
        }

        onKeyDown(event, highlightObject);
        break;
      case 'Shift':
      case 'Control':
      case 'Alt':
      case 'Meta':
      case 'CapsLock':
        event.preventDefault();
        break;
      default:
        onKeyDown(event, highlightObject);
        break;
    }
  };

  focusInput = () => {
    if (this.inputRef) {
      this.inputRef.focus();
    }
  };

  handleSelect = event => {
    const targetId = event.target.attributes.id.value;
    const index = targetId ? Number(targetId.split('-').pop()) : -1;
    this.selectedIndex = index;
    this.props.onChange(event, {
      selectedItem: this.items[index] || {},
      highlightedIndex: index,
      method: 'click'
    });
    if (this.props.multiselect) {
      this.focusInput();
    } else {
      this.handleMenuClose(() => {
        this.focusInput();
      });
    }
  };

  assignInputRef = c => {
    this.inputRef = c;
  };

  assignMenuListRef = c => {
    this.menuListRef = c;
  };

  handleIconMouseDown = event => {
    event.preventDefault();
  };

  handleIconClick = event => {
    event.preventDefault();
    event.stopPropagation();
    this.props.onClick(event);
    this.setState(
      state => ({
        open: !state.open
      }),
      () => {
        if (this.inputRef) {
          if (this.props.selectTextOnFocus) {
            this.inputRef.select();
          } else {
            this.inputRef.focus();
          }
        }
      }
    );
  };

  handleTextFieldClick = event => {
    this.props.onClick(event);
    this.setState(
      state => ({
        open: !state.open
      }),
      () => {
        if (
          this.props.selectTextOnFocus &&
          (this.props.multiselect || !this.state.open)
        ) {
          this.inputRef.select();
        }
      }
    );
  };

  handleTextFieldFocus = event => {
    this.props.onFocus(event);
    const input = event.target;
    if (input && !this.props.selectTextOnFocus) {
      const valueLength = input.value.length;
      input.setSelectionRange(valueLength, valueLength);
      // fix for non-chrome browsers
      // need wait a bit to do setSelectionRange one more time
      setTimeout(() => {
        input.setSelectionRange(valueLength, valueLength);
      });
    }
  };

  handleMenuMouseEnter = () => {
    this.setState({ highlightIndex: -1 });
  };

  renderCaret = theme => {
    const { disabled, errorText, warningText } = this.props;
    let infoType = null;
    if (errorText) {
      infoType = 'error';
    } else if (warningText) {
      infoType = 'warning';
    }

    return (
      <IconBox
        aria-hidden
        disabled={disabled}
        infoType={infoType}
        tabIndex="-1"
        theme={theme}
        onMouseDown={disabled ? undefined : this.handleIconMouseDown}
        onClick={disabled ? undefined : this.handleIconClick}
      >
        <DownIconWrapper
          disabled={disabled}
          infoType={infoType}
          theme={theme}
        />
      </IconBox>
    );
  };

  renderInputElement = inputProps => {
    const { open, highlightIndex } = this.state;
    const {
      ariaLabel,
      disabled,
      inputValue,
      suggestedText,
      theme,
      ...otherProps
    } = inputProps;
    const activedescendant =
      open && highlightIndex > -1 ? this.getMenuItemId(highlightIndex) : null;

    return (
      <TextFieldWrapper
        autoComplete="off"
        {...otherProps}
        theme={theme}
        aria-autocomplete={suggestedText ? 'both' : 'list'}
        aria-activedescendant={activedescendant}
        aria-controls={open ? this.menuId : null}
        aria-label={ariaLabel}
        assignInputRef={this.assignInputRef}
        disabled={disabled}
        addonAfter={this.renderCaret(theme)}
        id={this.ariaIdTextField}
        ghostText={suggestedText}
        tabIndex="0"
        value={inputValue || ''}
        onClick={disabled ? undefined : this.handleTextFieldClick}
        onFocus={disabled ? undefined : this.handleTextFieldFocus}
        onKeyDown={disabled ? undefined : this.handleTextFieldKeyDown}
        onChange={disabled ? undefined : this.handleTextFieldChange}
        onBlur={disabled ? undefined : this.handleTextFieldBlur}
      />
    );
  };

  renderAddNewItem = (addNewText, addNewItemProps, addNewValue, theme) => (
    <AddNewItem
      {...addNewItemProps}
      aria-label={`add new item ${
        addNewValue ? `${addNewValue}, press Enter key to add` : ''
      }`}
      theme={theme}
      value={addNewValue}
    >
      <AddNewIcon theme={theme} />
      <AddNewTextContainer>{addNewText}</AddNewTextContainer>
      {addNewValue && (
        <AddNewValueContainer>{addNewValue}</AddNewValueContainer>
      )}
    </AddNewItem>
  );

  renderItems = itemsProps => {
    const {
      dataSource,
      renderItem,
      addNew,
      addNewText,
      addNewItemProps,
      inputValue,
      theme
    } = itemsProps;
    if (!Array.isArray(dataSource)) {
      console.error('Error! "dataSource" prop must be an array.');
      return [];
    }

    if (typeof renderItem !== 'function') {
      console.error('Error! "renderItem" prop must be a function.');
      return [];
    }

    const addNewValue = this.getAddNewValue(inputValue);
    this.items = addNew
      ? [{ value: addNewValue, label: addNewValue }, ...dataSource]
      : dataSource;
    const itemsData = addNew
      ? [this.renderAddNewItem(addNewText, addNewItemProps, addNewValue, theme)]
      : [];
    itemsData.push(...dataSource.map((data, index) => renderItem(data, index)));
    return itemsData.map((item, index) => {
      const itemId = this.getMenuItemId(index);
      return React.cloneElement(item, {
        key: itemId,
        id: itemId
      });
    });
  };

  render() {
    const {
      'aria-label': ariaLabel,
      addNew,
      addNewText,
      addNewItemProps,
      showCheckmark,
      automationId,
      dataSource,
      disabled,
      errorText,
      id,
      inputValue,
      label,
      menuClass,
      onChange,
      onSearch,
      preventMenuOverflow,
      renderItem,
      theme,
      value,
      warningText,
      stylePosition,
      width,
      ...restProps
    } = this.props;
    const { open, highlightIndex } = this.state;

    const { currentTheme } = getTheme({ theme }, this.context);

    this.itemSelected = !!value;
    const inputProps = {
      ariaLabel,
      disabled,
      errorText,
      inputValue,
      label,
      theme: currentTheme,
      warningText,
      width,
      ...restProps
    };
    const inputElement = this.renderInputElement(inputProps);

    if (
      this.rootElement.current &&
      this.parentWidth !== this.rootElement.current.offsetWidth
    ) {
      this.parentWidth = this.rootElement.current.offsetWidth;
    }

    const itemsProps = {
      addNew,
      addNewItemProps,
      addNewText,
      dataSource,
      inputValue,
      renderItem,
      theme: currentTheme
    };
    const items = this.renderItems(itemsProps);

    return (
      <Wrapper
        ref={this.rootElement}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        data-automation-id={automationId}
        id={id}
        tabIndex="-1"
        width={width}
        {...restProps}
      >
        <MenuWrapper
          disableSearch
          scrollToSelected
          anchorElementRefPropName="assignInputRef"
          anchorElement={inputElement}
          aria-label={ariaLabel}
          className={menuClass}
          parentWidth={this.parentWidth}
          getSelectedIndex={this.handleGetSelectedIndex}
          highlightIndex={highlightIndex}
          id={this.menuId}
          menuItemRole="option"
          menuRole="listbox"
          open={open}
          preventMenuOverflow={preventMenuOverflow}
          reference={this.assignMenuListRef}
          selectedItem={value}
          showCheckmark={showCheckmark}
          stylePosition={stylePosition}
          withItems={items.length > 0}
          theme={currentTheme}
          onClose={this.handleMenuClose}
          onMouseEnter={this.handleMenuMouseEnter}
          onSelect={this.handleSelect}
        >
          {items}
        </MenuWrapper>
      </Wrapper>
    );
  }
}

DropdownTypeahead.displayName = 'DropdownTypeahead';
